/**
 * §F7 dashboard — pure chart-shaping over one `DashboardResponse`.
 *
 * No React and no fetch here, on the same principle `features/catalogue/catalogue.ts` and
 * `features/landing/overview.ts` already follow: `fetchDashboard` (Task 6) answers the
 * *whole* page in one payload, and every card is a plain function of that payload plus
 * whatever the reader has picked (a year override). Keeping the shaping pure means a chart
 * reading a given number can be asserted as arithmetic, not read back out of the DOM.
 *
 * The target-insight **sentences** are not built here. §F7's design direction moved that
 * job server-side (`apps.meta.dashboard.INSIGHT_TEXT`, both languages) so the dashboard's
 * `insights[]` already arrive as finished, translated text — this module has nothing to add
 * to them, and the dormant client-side `targetInsights` generator in
 * `features/landing/overview.ts` stays dormant (see that file's own note).
 */
import type { DashboardKpi, DashboardResponse, DashboardTargetRow } from '../../lib/api';
import type { LineSeries } from '../../components/charts/LineChart';
import type { Country } from '../../lib/state';

/** §F7's country palette for the trend chart — the same four countries, always in this order. */
export const CPAL: Record<'BFA' | 'MLI' | 'NER' | 'NGA', string> = {
  BFA: '#2E7D32',
  MLI: '#2B6CB0',
  NER: '#C77B21',
  NGA: '#8E3B62',
};

/** A KPI tile's ▲/▼ class — `''` for zero, `null` or `undefined` (no arrow, no colour). */
export function kpiDeltaClass(delta: number | null | undefined): 'up' | 'down' | '' {
  if (typeof delta !== 'number' || !Number.isFinite(delta) || delta === 0) return '';
  return delta > 0 ? 'up' : 'down';
}

/**
 * The targets-vs-achievement card, shaped for `BarsH`: `labels` and two index-aligned value
 * arrays — `series[0]` the target bar, `series[1]` the achieved bar, the Overview's
 * `targetChart` convention (target first, achieved second).
 *
 * `series[0]` is `null` for a `no_target` row **regardless of what `target` carries** — a
 * row this milestone has no committed number for must never grow one just because a future
 * payload's `target` field happens to hold a value. Every other status passes `target`
 * through unchanged: an `awaiting` row's `target` is a real commitment with `achieved: null`
 * (0 % — first results not in yet), which is a different sentence from "no target exists".
 */
export interface DashboardTargetRows {
  labels: string[];
  /** `[target, achieved]`, index-aligned with `labels`. */
  series: [(number | null)[], (number | null)[]];
  statuses: DashboardResponse['targets'][number]['status'][];
  pcts: (number | null)[];
}

export function targetRows(payload: DashboardResponse): DashboardTargetRows {
  const rows = payload.targets;
  return {
    labels: rows.map((r) => r.label),
    series: [
      rows.map((r) => (r.status === 'no_target' ? null : r.target)),
      // Same guard as the target column, mirrored: an `awaiting` row is "0% — first results
      // not in yet" (client rule 5) regardless of what the payload's own `achieved` happens
      // to carry, exactly as a `no_target` row's target is forced to `null` regardless of
      // what its `target` field carries.
      rows.map((r) => (r.status === 'awaiting' ? null : r.achieved)),
    ],
    statuses: rows.map((r) => r.status),
    pcts: rows.map((r) => (r.status === 'awaiting' ? 0 : r.pct)),
  };
}

/**
 * The cross-country trend card, shaped for `LineChart`: one series per country already in
 * the payload's scope, coloured by `CPAL` and carrying `values` (nulls included) untouched —
 * `LineChart` itself breaks the line across a gap rather than this module inventing one.
 */
export function trendSeries(payload: DashboardResponse): LineSeries[] {
  return payload.trend.series.map((s) => ({
    name: s.name,
    short: s.country,
    color: CPAL[s.country as keyof typeof CPAL] ?? '#8B958A',
    values: s.values,
  }));
}

/**
 * The year the dashboard reports at: the reader's local `dashYear` override when one is set,
 * else the map's own `selectYear`. `dashYear` is session-local (`lib/state.ts`'s `AppState`)
 * rather than the map's year, so browsing the dashboard's own year filter never moves the
 * map underneath the reader — see the store field's own comment for why it is not `y=`.
 */
export function resolveDashYear(storeYear: number, dashYear: number | null): number {
  return dashYear ?? storeYear;
}

/**
 * The downloaded CSV's filename — `apps.meta.views.dashboard_export`'s
 * `Content-Disposition: attachment; filename="soilvalues_dashboard_{scope}.csv"`, where
 * `scope` is the org unit's id when one is scoped (client item 7, backend `e441e7a`), else
 * the ISO3 country or the literal `"all"` for the whole programme.
 *
 * The unit wins over the country for the same reason it does on the wire: the server derives
 * the country from the unit, so a file named for the country would label one region's numbers
 * with the whole country's name.
 */
export function csvFilename(country: Country, unit?: string | null): string {
  if (unit) return `soilvalues_dashboard_${unit}.csv`;
  return `soilvalues_dashboard_${country === 'ALL' ? 'all' : country}.csv`;
}

/**
 * One line per indicator, out of the server's several sentences per indicator.
 *
 * Client, 2026-09-16 round 2: the "What the targets say" list ran to twenty-two bullets for
 * nine indicators — four of them for `B.2.1` alone — which is a wall of prose where the
 * reader wanted a summary. This is **presentation only**: `payload.insights` is untouched,
 * every sentence still reaches the CSV and the PDF, and nothing is rewritten here. Three
 * rules shape the list:
 *
 * 1. **Sentences are grouped by the indicator they name.** The server keys them by *kind*
 *    (`targetProgress`, `targetEop`, …) and repeats each key across indicators, so the
 *    grouping hook is the indicator `code` the sentence contains — matched longest-first, so
 *    `B.1.1` never captures a sentence about `B.1.11`. A sentence naming no code keeps its
 *    own line rather than being dropped.
 * 2. **An `awaiting` indicator contributes no sentence, only its code.** "X is at 0% of the
 *    15,000 target — awaiting first results" said six times is one fact, and the card prints
 *    it once as "6 indicators are awaiting first results: …".
 * 3. **A restatement is dropped.** `targetPace` ("6,320 more this year would meet B.2.1's
 *    target") carries the same number as `targetGap` ("B.2.1 is 6,320 short of the target")
 *    in the other direction. Everything else is joined with " · " onto the indicator's line,
 *    so no figure the server sent is lost from the page.
 */
export interface DashboardInsightSummary {
  /** One per measured indicator, in the payload's own order. `key` is `data-insight`. */
  lines: { key: string; text: string }[];
  /** The codes of the indicators that are awaiting first results, in payload order. */
  awaitingCodes: string[];
}

/** Insight keys that restate a figure another sentence in the same group already carries. */
const RESTATEMENT = /pace/i;

export function summariseInsights(payload: DashboardResponse): DashboardInsightSummary {
  // Longest code first: a payload carrying both `B.1.1` and `B.1.11` must not let the
  // shorter one swallow the longer one's sentences.
  const coded = payload.targets
    .filter((row): row is typeof row & { code: string } => Boolean(row.code))
    .slice()
    .sort((a, b) => b.code.length - a.code.length);
  const order = payload.targets.map((row) => row.id);
  const groups = new Map<string, { key: string; texts: string[] }>();
  const loose: { key: string; text: string }[] = [];

  for (const insight of payload.insights) {
    const owner = coded.find((row) => insight.text.includes(row.code));
    if (!owner) {
      loose.push({ key: insight.key, text: insight.text });
      continue;
    }
    if (owner.status === 'awaiting') {
      // Its code alone reaches the card; the sentence itself is the same one six times.
      groups.set(owner.id, groups.get(owner.id) ?? { key: insight.key, texts: [] });
      continue;
    }
    const group = groups.get(owner.id) ?? { key: insight.key, texts: [] };
    if (!RESTATEMENT.test(insight.key)) group.texts.push(insight.text);
    groups.set(owner.id, group);
  }

  const lines: { key: string; text: string }[] = [];
  for (const id of order) {
    const group = groups.get(id);
    if (!group || group.texts.length === 0) continue;
    lines.push({ key: group.key, text: group.texts.join(' · ') });
  }
  const awaitingCodes = payload.targets
    .filter((row) => Boolean(row.code) && row.status === 'awaiting' && groups.has(row.id))
    .map((row) => row.code as string);

  return { lines: [...lines, ...loose], awaitingCodes };
}


/* ------------------------------------------------- §1 the five numbered sections */

/**
 * Which numbered section an indicator belongs to (client, 2026-09-16: *"section it
 * systematically: i. Contractual indicators, ii. Outcome indicators, iii. Baseline
 * information, and the rest can follow"*).
 *
 * The payload's own `tier` wins. It is optional on the type (`DashboardKpi.tier`) because a
 * reader can be holding a `/dashboard` answer cached from before the field existed, and a
 * page that could not section such a payload would be a blank dashboard rather than a stale
 * one — so a code map stands behind it: the four indicators IFDC committed to in the grant
 * agreement are `B.2.1`, `C.2.1`, `D.2` and `E.1`; the results-framework outcome indicators
 * are the `B.1.*`, `C.1.*` and `D.1` family. Anything else — an indicator with no code at
 * all, such as the ISFM series — is `other` and reads in section IV.
 *
 * The fallback is a *last resort*, not a second opinion: it is consulted only where `tier`
 * is absent, so the day the server sends a tier this map disagrees with, the server wins.
 */
export type DashboardTier = 'contractual' | 'outcome' | 'other';

const CONTRACTUAL_CODES = new Set(['B.2.1', 'C.2.1', 'D.2', 'E.1']);
/** `B.1`, `B.1.3`, `C.1.2`, `D.1` — the family, not a prefix match on `D.11`. */
const OUTCOME_CODES = /^(?:B\.1|C\.1|D\.1)(?:\.\d+)*$/;

export function tierOf(row: { tier?: DashboardTier; code?: string | null }): DashboardTier {
  if (row.tier === 'contractual' || row.tier === 'outcome' || row.tier === 'other') return row.tier;
  const code = row.code ?? '';
  if (CONTRACTUAL_CODES.has(code)) return 'contractual';
  if (OUTCOME_CODES.test(code)) return 'outcome';
  return 'other';
}

/** One tier's share of the payload — the tiles above, the target rows below. */
export interface DashboardTierRows {
  kpis: DashboardKpi[];
  targets: DashboardTargetRow[];
}

export interface DashboardTierSplit {
  contractual: DashboardTierRows;
  outcome: DashboardTierRows;
  other: DashboardTierRows;
}

/**
 * The payload's KPI rail and target rows, split three ways in the payload's **own order**.
 *
 * Nothing is dropped and nothing is duplicated: every row lands in exactly one tier, so the
 * five sections between them print the same figures the one-rail page printed.
 */
export function splitByTier(
  kpis: DashboardKpi[],
  targets: DashboardTargetRow[],
): DashboardTierSplit {
  const split: DashboardTierSplit = {
    contractual: { kpis: [], targets: [] },
    outcome: { kpis: [], targets: [] },
    other: { kpis: [], targets: [] },
  };
  for (const kpi of kpis) split[tierOf(kpi)].kpis.push(kpi);
  for (const row of targets) split[tierOf(row)].targets.push(row);
  return split;
}

/** The three sections, in the order the client numbered them (2026-09-16 round 3). */
export type DashboardSectionKey = 'contractual' | 'outcome' | 'baseline';

export interface DashboardSection {
  key: DashboardSectionKey;
  /** The numeral printed in the section's disc and on its nav pill. */
  numeral: string;
}

export const sectionSpec: readonly DashboardSection[] = [
  { key: 'contractual', numeral: 'I' },
  { key: 'outcome', numeral: 'II' },
  { key: 'baseline', numeral: 'III' },
] as const;

/**
 * A `/dashboard` payload with one tier's target rows in place of the whole set.
 *
 * The chart and the table cards are pure functions of `payload.targets` (`targetRows`), so
 * a section shows its own rows by handing them a payload narrowed to them rather than by
 * growing a filter argument on every card.
 */
export function withTargets(
  payload: DashboardResponse,
  targets: DashboardTargetRow[],
): DashboardResponse {
  return { ...payload, targets };
}


/* ------------------------------------------------- §1 section II — one card per outcome */

/**
 * The outcome section's cards (client, 2026-09-16 round 3: *"the outcome indicators as
 * cards, all of them"*).
 *
 * The **target rows are the spine**, not the KPI rail: `targets[]` carries every
 * results-framework outcome indicator the programme committed to, while `kpis[]` carries
 * only those with a figure worth a tile — so a section built from the rail alone would print
 * the two that are measured and silently drop the five that are not, which is the opposite
 * of what section II is for. A KPI with no row of its own is appended rather than dropped,
 * so nothing the payload carries leaves the page.
 *
 * `status` follows the **row**, exactly as the old tile's pill did: the row is the authority
 * on whether an indicator has been measured, and a card saying "measured" beside a row that
 * says otherwise would be the page contradicting itself. Only a KPI with no row at all falls
 * back to whether it has a figure.
 *
 * Nothing is invented: a `no_target` row keeps `target: null` (the card prints "no target
 * set"), an `awaiting` row keeps `value: null` whatever `achieved` happens to hold, and the
 * sparkline is the KPI's own or nothing.
 */
export interface DashboardOutcomeCard {
  id: string;
  code: string | null;
  label: string;
  unit: string;
  fmt: number;
  /** The measured figure, or `null` while the indicator is awaiting first results. */
  value: number | null;
  /** The commitment, or `null` where the payload says there is none. */
  target: number | null;
  /** Achieved as a percentage of that commitment; `null` where there is no commitment. */
  pct: number | null;
  status: 'measured' | 'awaiting';
  /** The KPI's six-year series, when it has one — `null` for a row with no rail entry. */
  spark: (number | null)[] | null;
  /** The domain the colour comes from, or `null` where only a target row exists. */
  domain: string | null;
}

export function outcomeCards(
  kpis: DashboardKpi[],
  targets: DashboardTargetRow[],
): DashboardOutcomeCard[] {
  const byId = new Map(kpis.map((kpi) => [kpi.id, kpi]));
  const cards: DashboardOutcomeCard[] = targets.map((row) => {
    const kpi = byId.get(row.id);
    const measured = row.achieved !== null;
    return {
      id: row.id,
      code: row.code,
      label: row.label,
      unit: kpi?.unit ?? row.unit,
      fmt: kpi?.fmt ?? 0,
      // The rail's figure where the rail has one and means it; the row's own otherwise.
      value: measured ? (kpi?.active && kpi.value !== null ? kpi.value : row.achieved) : null,
      target: row.status === 'no_target' ? null : row.target,
      pct: row.status === 'measured' ? row.pct : row.status === 'awaiting' ? 0 : null,
      status: measured ? 'measured' : 'awaiting',
      spark: kpi?.spark ?? null,
      domain: kpi?.domain ?? null,
    };
  });
  for (const kpi of kpis) {
    if (targets.some((row) => row.id === kpi.id)) continue;
    const measured = kpi.active && kpi.value !== null;
    cards.push({
      id: kpi.id,
      code: kpi.code ?? null,
      label: kpi.label,
      unit: kpi.unit,
      fmt: kpi.fmt,
      value: measured ? kpi.value : null,
      target: null,
      pct: null,
      status: measured ? 'measured' : 'awaiting',
      spark: kpi.spark,
      domain: kpi.domain,
    });
  }
  return cards;
}


/* ------------------------------------------------ §1 section I — the grouped bar chart */

/**
 * One group per **contractual indicator**, for the section I chart (client, 2026-09-16
 * round 3: *"one group per contractual indicator — B.2.1, C.2.1, D.2, E.1"*).
 *
 * The rail is the spine here, the mirror image of `outcomeCards`: the client named the four
 * indicators the chart must show, and `targets[]` carries a row only where the server
 * computed a commitment for the reporting year — at any year but the target year it carries
 * none at all. A chart built from the rows alone would therefore show two groups in 2025 and
 * none in 2024, with the reader left to guess whether the missing indicators were achieved
 * or simply not committed. Every indicator gets a group; what it has to say is in its
 * `status`, and a group with no commitment draws no bar rather than a bar at zero.
 *
 * A target row with no rail entry is appended, so a payload whose rows outrun its rail
 * (a domain filter, a cached answer) still draws every commitment it carries.
 */
export interface DashboardTargetGroup {
  id: string;
  /** The indicator code — what labels the group; the names are on the tiles above it. */
  code: string;
  label: string;
  unit: string;
  achieved: number | null;
  target: number | null;
  /** Achieved as a percentage of the target; `null` unless the row is `measured`. */
  pct: number | null;
  status: DashboardTargetRow['status'] | 'awaiting';
  /** The server split a programme commitment across this scope — a weaker claim. */
  apportioned: boolean;
}

export function targetGroups(
  kpis: DashboardKpi[],
  targets: DashboardTargetRow[],
): DashboardTargetGroup[] {
  const rowOf = new Map(targets.map((row) => [row.id, row]));
  const group = (
    id: string,
    code: string,
    label: string,
    unit: string,
    row: DashboardTargetRow | undefined,
    kpi: DashboardKpi | undefined,
  ): DashboardTargetGroup => {
    if (!row) {
      /*
        No commitment in this payload — which on a chart *about commitments* is "no target
        set", whether or not the indicator has a figure of its own (review, 2026-09-16).
        "Awaiting first results" is reserved for the other case: a real target with nothing
        achieved against it yet, which is a row the server did send.
      */
      const measured = Boolean(kpi?.active) && (kpi?.value ?? null) !== null;
      return {
        id,
        code,
        label,
        unit,
        achieved: measured ? (kpi?.value ?? null) : null,
        target: null,
        pct: null,
        status: 'no_target',
        apportioned: false,
      };
    }
    return {
      id,
      code,
      label,
      unit,
      // The same two guards `targetRows` applies, for the same reasons: an awaiting row
      // has no achievement whatever `achieved` holds, a no_target row has no commitment.
      achieved: row.status === 'awaiting' ? null : row.achieved,
      target: row.status === 'no_target' ? null : row.target,
      pct: row.status === 'measured' ? row.pct : null,
      status: row.status,
      apportioned: row.target_source === 'apportioned',
    };
  };

  const groups = kpis.map((kpi) =>
    group(kpi.id, kpi.code ?? kpi.id, kpi.label, kpi.unit, rowOf.get(kpi.id), kpi),
  );
  for (const row of targets) {
    if (kpis.some((kpi) => kpi.id === row.id)) continue;
    groups.push(group(row.id, row.code, row.label, row.unit, row, undefined));
  }
  return groups;
}
